import { Button, NativeSelect, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { File, MessageQueryParams, MessageResponse } from '@/api';
import { periodStart } from '@/components/FormMessages/days';
import classes from '@/components/Inbox/Inbox.module.css';
import { MessageRow } from '@/components/Inbox/MessageRow';
import { ReadingPane } from '@/components/Inbox/ReadingPane';
import { openConfirmDelete } from '@/components/Ui/ConfirmDelete';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { useUndoToast } from '@/components/Ui/Toast';
import { UiLink } from '@/components/Ui/UiLink';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import {
  isPeriod,
  isSpamFor,
  isVerdict,
  type Period,
  usePeriodPreference,
  useVerdictPreference,
  type Verdict,
} from '@/lib/message-prefs';
import { isNostr, type MessageData } from '@/lib/messages';
import { getCachedDecryptedMessage } from '@/lib/nostr-decrypt';
import { useRustyState } from '@/state';

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;

interface Filters {
  form: string;
  verdict: Verdict;
  period: Period;
  unread: boolean;
  q: string;
}

type ListState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; items: MessageResponse[]; files: File[]; total: number };

function toQuery(f: Filters, after?: MessageResponse): MessageQueryParams {
  return {
    limit: PAGE_SIZE,
    ...(after ? { before: after.created_at, before_id: after.id } : {}),
    form_id: f.form || undefined,
    is_spam: isSpamFor(f.verdict),
    since: f.period === 'all' ? undefined : periodStart(Number(f.period)),
    unread: f.unread || undefined,
    q: f.q || undefined,
  };
}

interface Changes {
  seq: number;
  byId: Map<string, { seq: number; message: MessageResponse }>;
}

/** Rows changed here win over a list response whose request started before the change. */
function withChanges(changes: Changes, items: MessageResponse[], started: number) {
  return items.map((m) => {
    const change = changes.byId.get(m.id);
    return change && change.seq > started ? change.message : m;
  });
}

async function cachedDecryptions(items: MessageResponse[]): Promise<Record<string, MessageData>> {
  const out: Record<string, MessageData> = {};
  await Promise.all(
    items.filter(isNostr).map(async (m) => {
      const data = await getCachedDecryptedMessage(m.id).catch(() => null);
      if (data) out[m.id] = data;
    })
  );
  return out;
}

export function InboxScreen() {
  const { t } = useTranslation();
  const { id: selectedId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const notify = useUndoToast();
  const forms = useRustyState((s) => s.formsSummary);

  const [savedVerdict, saveVerdict] = useVerdictPreference();
  const [savedPeriod, savePeriod] = usePeriodPreference();
  // A link's own filter wins; without one, the last choice made here. One form's list spans all time.
  const verdictParam = params.get('verdict');
  const periodParam = params.get('period');
  const formParam = params.get('form') ?? '';
  const filters: Filters = {
    form: formParam,
    verdict: isVerdict(verdictParam) ? verdictParam : savedVerdict,
    period: isPeriod(periodParam) ? periodParam : formParam ? 'all' : savedPeriod,
    unread: params.get('unread') === '1',
    q: params.get('q') ?? '',
  };
  const { form, verdict, period, unread, q } = filters;

  const [search, setSearch] = useState(q);
  const [searchFor, setSearchFor] = useState(q);
  const searchTimer = useRef<number | undefined>(undefined);
  if (q !== searchFor) {
    setSearchFor(q);
    if (search.trim() !== q) setSearch(q);
  }
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);
  const [decrypted, setDecrypted] = useState<Record<string, MessageData>>({});
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const lastChecked = useRef<string | null>(null);
  const [reload, setReload] = useState(0);
  const changes = useRef<Changes>({ seq: 0, byId: new Map() });
  const generation = useRef(0);

  useEffect(() => {
    void useRustyState.getState().refreshFormsSummary();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload only re-runs the fetch
  useEffect(() => {
    let ignore = false;
    generation.current += 1;
    setLoadingMore(false);
    const started = changes.current.seq;
    useRustyState
      .getState()
      .api.listMessages(toQuery({ form, verdict, period, unread, q }))
      .then(async (res) => {
        const items = withChanges(changes.current, res.data ?? [], started);
        const cache = await cachedDecryptions(items);
        if (ignore) return;
        setList({
          status: 'ready',
          items,
          files: res.files ?? [],
          total: res.total ?? items.length,
        });
        setDecrypted((prev) => ({ ...prev, ...cache }));
      })
      .catch(() => {
        if (!ignore) setList({ status: 'error' });
      });
    return () => {
      ignore = true;
    };
  }, [form, verdict, period, unread, q, reload]);

  const formTitle = (formId: string) => forms?.find((f) => f.id === formId)?.title;

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setChecked(new Set());
    setParams(next, { replace: true });
  };

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setSearch(value);
    window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => setParam('q', value.trim()), SEARCH_DELAY_MS);
  };

  const listPath = () => `${createLanguageURL('/account/messages')}${location.search}`;
  const open = (id: string) =>
    navigate(`${createLanguageURL(`/account/messages/${id}`)}${location.search}`);

  const loadMore = async () => {
    if (list.status !== 'ready') return;
    const gen = generation.current;
    setLoadingMore(true);
    const started = changes.current.seq;
    try {
      const res = await useRustyState
        .getState()
        .api.listMessages(toQuery(filters, list.items[list.items.length - 1]));
      const items = withChanges(changes.current, res.data ?? [], started);
      const cache = await cachedDecryptions(items);
      if (gen !== generation.current) return;
      setList((prev) =>
        prev.status === 'ready'
          ? {
              status: 'ready',
              items: [
                ...prev.items,
                ...items.filter((m) => !prev.items.some((p) => p.id === m.id)),
              ],
              files: [...prev.files, ...(res.files ?? [])],
              total: res.total ?? prev.total,
            }
          : prev
      );
      setDecrypted((prev) => ({ ...prev, ...cache }));
    } catch {
      // the API layer reports the error
    } finally {
      if (gen === generation.current) setLoadingMore(false);
    }
  };

  const updateItems = (fn: (items: MessageResponse[]) => MessageResponse[], removed = 0) =>
    setList((prev) =>
      prev.status === 'ready'
        ? { ...prev, items: fn(prev.items), total: prev.total - removed }
        : prev
    );

  const onUpdated = (message: MessageResponse) => {
    changes.current.seq += 1;
    changes.current.byId.set(message.id, { seq: changes.current.seq, message });
    // A message opened by link that the loaded list does not hold yet: the list is out of date.
    if (list.status === 'ready' && !list.items.some((m) => m.id === message.id)) {
      setReload((n) => n + 1);
      return;
    }
    updateItems((items) => items.map((m) => (m.id === message.id ? message : m)));
  };

  const onDeleted = (id: string) => {
    updateItems((items) => items.filter((m) => m.id !== id), 1);
    navigate(listPath(), { replace: true });
  };

  const onDecrypted = (id: string, data: MessageData) =>
    setDecrypted((prev) => ({ ...prev, [id]: data }));

  const loadedIds = list.status === 'ready' ? list.items.map((m) => m.id) : [];
  const checkedItems = list.status === 'ready' ? list.items.filter((m) => checked.has(m.id)) : [];
  const allChecked = loadedIds.length > 0 && loadedIds.every((id) => checked.has(id));
  const someChecked = !allChecked && loadedIds.some((id) => checked.has(id));

  const toggleChecked = (id: string, on: boolean, range: boolean) => {
    const from = range && lastChecked.current ? loadedIds.indexOf(lastChecked.current) : -1;
    const to = loadedIds.indexOf(id);
    const span =
      from >= 0 && to >= 0 ? loadedIds.slice(Math.min(from, to), Math.max(from, to) + 1) : [id];
    lastChecked.current = id;
    setChecked((prev) => {
      const next = new Set(prev);
      for (const i of span) {
        if (on) next.add(i);
        else next.delete(i);
      }
      return next;
    });
  };

  const checkAll = (on: boolean) => {
    lastChecked.current = null;
    setChecked(on ? new Set(loadedIds) : new Set());
  };

  const bulkMark = async (isSpam: boolean) => {
    const ids = checkedItems.filter((m) => m.is_spam !== isSpam).map((m) => m.id);
    if (ids.length === 0) return;
    try {
      const res = await useRustyState
        .getState()
        .api.bulkUpdateMessages({ message_ids: ids, is_spam: isSpam });
      const failed = new Set(res.failed);
      const changed = new Set(ids.filter((id) => !failed.has(id)));
      updateItems((items) =>
        items.map((m) =>
          changed.has(m.id) ? { ...m, is_spam: isSpam, user_marked_spam: true } : m
        )
      );
      const parts = [
        t(isSpam ? 'inbox.bulkMarkedSpam' : 'inbox.bulkMarkedNotSpam', { count: res.succeeded }),
      ];
      if (!isSpam) parts.push(t('inbox.bulkNotified', { count: res.notified_recipients }));
      if (res.integrations_resent > 0)
        parts.push(t('inbox.bulkIntegrationsResent', { count: res.integrations_resent }));
      if (res.auto_replies_sent > 0)
        parts.push(t('inbox.bulkAutoRepliesSent', { count: res.auto_replies_sent }));
      if (failed.size > 0) parts.push(t('inbox.bulkFailed', { count: failed.size }));
      notify(parts.join(' '));
      setChecked(new Set());
      void useRustyState.getState().refreshUnreadCount();
      void useRustyState.getState().refreshFormsSummary();
    } catch {
      // the API layer reports the error
    }
  };

  // Spam carries no unread marker, so it is never "unread" here either.
  const unreadChecked = checkedItems.filter((m) => !m.is_read && !m.is_spam);

  const bulkRead = async () => {
    const ids = unreadChecked.map((m) => m.id);
    if (ids.length === 0) return;
    try {
      const res = await useRustyState
        .getState()
        .api.bulkUpdateMessages({ message_ids: ids, is_read: true });
      const failed = new Set(res.failed);
      const changed = new Set(ids.filter((id) => !failed.has(id)));
      updateItems((items) => items.map((m) => (changed.has(m.id) ? { ...m, is_read: true } : m)));
      const parts = [t('inbox.markedRead', { count: res.succeeded })];
      if (failed.size > 0) parts.push(t('inbox.bulkFailed', { count: failed.size }));
      notify(parts.join(' '));
      setChecked(new Set());
      void useRustyState.getState().refreshUnreadCount();
    } catch {
      // the API layer reports the error
    }
  };

  const markAllRead = () => {
    if (list.status !== 'ready') return;
    const count = list.total;
    modals.openConfirmModal({
      title: t('inbox.markAllReadTitle', { count }),
      children: <span className={classes.sub}>{t('inbox.markAllReadDescription')}</span>,
      labels: { confirm: t('inbox.markAllReadConfirm'), cancel: t('ui.cancel') },
      confirmProps: { variant: 'filled' },
      onConfirm: () => {
        void useRustyState
          .getState()
          .api.markMessagesRead({
            form_id: form || undefined,
            is_spam: toQuery(filters).is_spam,
            since: toQuery(filters).since,
            q: q || undefined,
          })
          .then((res) => {
            notify(t('inbox.markedRead', { count: res.updated }));
            setChecked(new Set());
            setReload((n) => n + 1);
            void useRustyState.getState().refreshUnreadCount();
          })
          .catch(() => {
            // the API layer reports the error
          });
      },
    });
  };

  const bulkDelete = () => {
    const ids = [...checked];
    openConfirmDelete({
      label: t('inbox.bulkDeleteTitle', { count: ids.length }),
      description: t('inbox.bulkDeleteDescription'),
      confirmLabel: t('inbox.bulkDeleteConfirm', { count: ids.length }),
      onConfirm: async () => {
        const res = await useRustyState.getState().api.bulkDeleteMessages(ids);
        const failed = new Set(res.failed);
        const gone = new Set(ids.filter((id) => !failed.has(id)));
        updateItems((items) => items.filter((m) => !gone.has(m.id)), gone.size);
        setChecked(new Set());
        void useRustyState.getState().refreshUnreadCount();
        void useRustyState.getState().refreshFormsSummary();
        const parts = [t('inbox.bulkDeleted', { count: res.succeeded })];
        if (failed.size > 0) parts.push(t('inbox.bulkFailed', { count: failed.size }));
        notify(parts.join(' '));
        if (selectedId && gone.has(selectedId)) navigate(listPath(), { replace: true });
      },
    });
  };

  const countKey =
    period === '7' ? 'inbox.count7' : period === '30' ? 'inbox.count30' : 'inbox.countAll';
  const anyFilter = !!(form || verdict || unread || q);
  const formOptions = [...(forms ?? [])]
    .sort((a, b) => a.title.localeCompare(b.title))
    .map((f) => ({ value: f.id, label: f.title }));

  return (
    <Page width="wide">
      <PageHeader
        title={t('inbox.pageTitle')}
        actions={<UiLink to="/account/messages/volume">{t('inbox.volumeLink')}</UiLink>}
      />

      <div className={classes.toolbar}>
        <TextInput
          type="search"
          className={classes.search}
          aria-label={t('inbox.searchLabel')}
          placeholder={t('inbox.searchPlaceholder')}
          value={search}
          onChange={onSearch}
        />
        <NativeSelect
          aria-label={t('inbox.formLabel')}
          value={form}
          onChange={(e) => setParam('form', e.currentTarget.value)}
          data={[{ value: '', label: t('inbox.allForms') }, ...formOptions]}
        />
        <NativeSelect
          aria-label={t('inbox.verdictLabel')}
          value={verdict}
          onChange={(e) => {
            const value = e.currentTarget.value as Verdict;
            saveVerdict(value);
            setParam('verdict', value);
          }}
          data={[
            { value: '', label: t('inbox.verdictAll') },
            { value: 'not_spam', label: t('inbox.verdictNotSpam') },
            { value: 'spam', label: t('inbox.verdictSpam') },
          ]}
        />
        <NativeSelect
          aria-label={t('inbox.periodLabel')}
          value={period}
          onChange={(e) => {
            const value = e.currentTarget.value as Period;
            savePeriod(value);
            setParam('period', value);
          }}
          data={[
            { value: '7', label: t('inbox.period7') },
            { value: '30', label: t('inbox.period30') },
            { value: 'all', label: t('inbox.periodAll') },
          ]}
        />
        <label className={classes.unreadToggle}>
          <input
            type="checkbox"
            className={classes.checkbox}
            checked={unread}
            onChange={(e) => setParam('unread', e.currentTarget.checked ? '1' : '')}
          />
          {t('inbox.unreadOnly')}
        </label>
      </div>

      {checked.size > 0 && (
        <div className={classes.bulk} data-bulk-bar>
          <strong className={classes.bulkCount}>
            {t('inbox.selected', { count: checked.size })}
          </strong>
          {checkedItems.some((m) => !m.is_spam) && (
            <Button size="xs" onClick={() => void bulkMark(true)}>
              {t('inbox.bulkSpam')}
            </Button>
          )}
          {checkedItems.some((m) => m.is_spam) && (
            <Button size="xs" onClick={() => void bulkMark(false)}>
              {t('inbox.bulkNotSpam')}
            </Button>
          )}
          {unreadChecked.length > 0 && (
            <Button size="xs" onClick={() => void bulkRead()}>
              {t('inbox.bulkRead')}
            </Button>
          )}
          <Button size="xs" variant="danger" onClick={bulkDelete}>
            {t('inbox.bulkDelete')}
          </Button>
          <Button size="xs" className={classes.bulkClear} onClick={() => setChecked(new Set())}>
            {t('inbox.bulkClear')}
          </Button>
        </div>
      )}

      <div className={selectedId ? `${classes.panes} ${classes.hasSelection}` : classes.panes}>
        <section className={classes.list} aria-label={t('inbox.listLabel')}>
          {list.status === 'loading' && <div className={classes.listEmpty}>{t('ui.loading')}</div>}
          {list.status === 'error' && (
            <div className={classes.listEmpty}>{t('inbox.loadFailed')}</div>
          )}
          {list.status === 'ready' && list.items.length === 0 && (
            <div className={classes.listEmpty}>
              {anyFilter || period !== 'all' ? t('inbox.empty') : t('inbox.emptyAll')}
            </div>
          )}
          {list.status === 'ready' && list.items.length > 0 && (
            <div className={classes.listHead}>
              <label className={classes.selectAll}>
                <input
                  type="checkbox"
                  className={classes.checkbox}
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = someChecked;
                  }}
                  onChange={(e) => checkAll(e.currentTarget.checked)}
                  aria-label={t('inbox.selectAll')}
                />
              </label>
              {verdict !== 'spam' && (
                <Button size="xs" variant="subtle" onClick={markAllRead}>
                  {t('inbox.markAllRead', { count: list.total })}
                </Button>
              )}
            </div>
          )}
          {list.status === 'ready' &&
            list.items.map((m) => (
              <MessageRow
                key={m.id}
                message={m}
                formTitle={formTitle(m.form_id)}
                files={list.files.filter((f) => f.message_id === m.id).length}
                decrypted={decrypted[m.id]}
                selected={m.id === selectedId}
                checked={checked.has(m.id)}
                onOpen={() => open(m.id)}
                onCheck={(on, range) => toggleChecked(m.id, on, range)}
              />
            ))}
          {list.status === 'ready' && list.items.length > 0 && (
            <div className={classes.listFooter}>
              <span className={classes.small}>
                {t(countKey, { shown: list.items.length, count: list.total })}
              </span>
              {list.items.length < list.total && (
                <Button size="xs" onClick={() => void loadMore()} loading={loadingMore}>
                  {t('inbox.loadMore')}
                </Button>
              )}
            </div>
          )}
        </section>

        <article
          className={selectedId ? classes.pane : `${classes.pane} ${classes.paneEmpty}`}
          aria-label={t('inbox.paneLabel')}
        >
          {selectedId ? (
            <>
              <span className={classes.back}>
                <UiLink to={`/account/messages${location.search}`}>{t('inbox.backToList')}</UiLink>
              </span>
              <ReadingPane
                key={selectedId}
                id={selectedId}
                formTitle={formTitle}
                decrypted={decrypted[selectedId]}
                onDecrypted={onDecrypted}
                onUpdated={onUpdated}
                onDeleted={onDeleted}
              />
            </>
          ) : (
            <span className={classes.sub}>{t('inbox.selectPrompt')}</span>
          )}
        </article>
      </div>
    </Page>
  );
}
